import { Check, Clock, X } from 'lucide-react';
import { useState } from 'react';
import { CapacityBracket } from '../../../types/capacity-brackets.types';
import { getDurationLabel } from '../../../utils/capacity-brackets.utils';

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: '#374151',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  outline: 'none',
  backgroundColor: '#ffffff',
  color: '#111827',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const btnPrimaryStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '9px 18px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#ffffff',
  backgroundColor: '#00758d',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const btnCancelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '9px 18px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151',
  backgroundColor: '#ffffff',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  cursor: 'pointer',
};

export function BracketForm({
  initial,
  onSubmit,
  onCancel,
  submitLabel = 'Ajouter',
  data,
}: {
  initial: CapacityBracket;
  onSubmit: (b: CapacityBracket) => void;
  onCancel: () => void;
  submitLabel?: string;
  data: any;
}) {
  const [draft, setDraft] = useState<CapacityBracket>({ ...initial });

  const set = (patch: Partial<CapacityBracket>) => setDraft((prev) => ({ ...prev, ...patch }));

  // Déterminer quelles durées sont activées à l'étape 4
  const enabledDurations = {
    HALF_DAY: data.allowHalfDay !== false,
    FULL_DAY: data.allowFullDay !== false,
    MONTH: !!data.allowMonth,
  };

  const switchType = (type: 'forfait' | 'duration') => {
    set({
      bracketType: type,
      prices:
        type === 'forfait'
          ? [{ durationType: 'FORFAIT', price: 100000 }]
          : Object.entries(enabledDurations)
              .filter(([_, enabled]) => enabled)
              .map(([type]) => ({
                durationType: type as any,
                price:
                  type === 'HALF_DAY' ? 100000 : type === 'FULL_DAY' ? 150000 : type === 'MONTH' ? 3000000 : 100000,
              })),
    });
  };

  const setPrice = (durationType: string, price: number) => {
    setDraft((prev) => ({
      ...prev,
      prices: prev.prices.map((p) => (p.durationType === durationType ? { ...p, price } : p)),
    }));
  };

  const handleFinalSubmit = () => {
    // Filtrer les prix une dernière fois avant de soumettre
    const finalPrices = draft.prices.filter((p) => {
      if (draft.bracketType === 'forfait') return p.durationType === 'FORFAIT';
      return (enabledDurations as any)[p.durationType];
    });
    onSubmit({ ...draft, prices: finalPrices });
  };

  const visiblePrices = draft.prices.filter((p) => {
    if (draft.bracketType === 'forfait') return p.durationType === 'FORFAIT';
    return (enabledDurations as any)[p.durationType];
  });

  const isValid = draft.label.trim() !== '' && draft.minCapacity < draft.maxCapacity && visiblePrices.length > 0;

  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>
            Label de la tranche <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            style={inputStyle}
            value={draft.label}
            onChange={(e) => set({ label: e.target.value })}
            placeholder="Ex : 6 à 10 personnes"
          />
        </div>
        <div>
          <label style={labelStyle}>Nom de la salle / espace</label>
          <input
            style={inputStyle}
            value={draft.roomName || ''}
            onChange={(e) => set({ roomName: e.target.value })}
            placeholder="Ex : Salle de conférence"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>
            Capacité min <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="number"
            style={inputStyle}
            value={draft.minCapacity}
            min={1}
            onChange={(e) => set({ minCapacity: parseInt(e.target.value) || 1 })}
          />
        </div>
        <div>
          <label style={labelStyle}>
            Capacité max <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="number"
            style={inputStyle}
            value={draft.maxCapacity}
            min={draft.minCapacity + 1}
            onChange={(e) => set({ maxCapacity: parseInt(e.target.value) || 10 })}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={labelStyle}>Type de tarification</label>
          <select
            style={selectStyle}
            value={draft.bracketType}
            onChange={(e) => switchType(e.target.value as 'forfait' | 'duration')}
          >
            <option value="duration">Par durée (demi / journée)</option>
            <option value="forfait">Forfait unique</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Unité de prix</label>
          <select
            style={selectStyle}
            value={draft.pricingUnit}
            onChange={(e) => set({ pricingUnit: e.target.value as 'per_group' | 'per_person' })}
          >
            <option value="per_group">Par groupe</option>
            <option value="per_person">Par personne</option>
          </select>
        </div>
      </div>

      <div
        style={{
          padding: '20px',
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Clock size={18} color="#00758d" />
          <label style={{ ...labelStyle, marginBottom: 0, fontSize: '14px', fontWeight: 600 }}>
            Définition des Prix (FCFA)
          </label>
          {draft.pricingUnit === 'per_person' && (
            <span
              style={{
                fontSize: '12px',
                color: '#6b7280',
                fontWeight: 400,
                background: '#f3f4f6',
                padding: '2px 8px',
                borderRadius: '4px',
                marginLeft: 'auto',
              }}
            >
              Tarification par personne
            </span>
          )}
        </div>

        {draft.bracketType === 'forfait' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              background: '#f8fafb',
              borderRadius: '8px',
              border: '1px solid #f1f5f9',
            }}
          >
            <label style={{ flex: 1, fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>
              Prix forfaitaire unique
            </label>
            <div style={{ position: 'relative', width: '220px' }}>
              <input
                type="number"
                style={{ ...inputStyle, paddingRight: '50px', textAlign: 'right', fontWeight: 600 }}
                value={draft.prices.find((p) => p.durationType === 'FORFAIT')?.price ?? 0}
                min={0}
                step={1000}
                onChange={(e) => setPrice('FORFAIT', parseInt(e.target.value) || 0)}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '11px',
                  color: '#9ca3af',
                  fontWeight: 700,
                }}
              >
                XAF
              </span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {visiblePrices.map((p) => (
              <div
                key={p.durationType}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 14px',
                  background: '#f8fafb',
                  borderRadius: '10px',
                  border: '1px solid #f1f5f9',
                }}
              >
                <label
                  style={{
                    flex: 1,
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#4b5563',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getDurationLabel(p.durationType)}
                </label>
                <div style={{ position: 'relative', width: '160px', flexShrink: 0 }}>
                  <input
                    type="number"
                    style={{
                      ...inputStyle,
                      height: '36px',
                      paddingRight: '50px',
                      textAlign: 'right',
                      fontWeight: 600,
                      border: '1px solid #e2e8f0',
                    }}
                    value={p.price}
                    min={0}
                    step={1000}
                    onChange={(e) => setPrice(p.durationType, parseInt(e.target.value) || 0)}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '10px',
                      color: '#9ca3af',
                      fontWeight: 700,
                    }}
                  >
                    XAF
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button type="button" style={btnCancelStyle} onClick={onCancel}>
          <X size={14} /> Annuler
        </button>
        <button
          type="button"
          style={{ ...btnPrimaryStyle, opacity: isValid ? 1 : 0.5, cursor: isValid ? 'pointer' : 'not-allowed' }}
          disabled={!isValid}
          onClick={() => isValid && handleFinalSubmit()}
        >
          <Check size={14} /> {submitLabel}
        </button>
      </div>
    </div>
  );
}
